import React, { FC, useState, useMemo } from "react";
import {
  countries as countriesConfig,
  countryAbbreviationMapper as countryAbbreviationMapperConfig
} from "@RHCommerceDev/resources/countries-config.json";
import useUserPreferences from "hooks/useUserPreferences";
import { useEnv } from "hooks/useEnv";
import yn from "yn";
import { processEnvServer } from "hooks/useSsrHooks";
import { useCountrySite } from "customProviders/CountrySiteProvider";
import useSite from "hooks/useSite";
import { useCookiesWithPermission } from "hooks/useCookiesWithPermission";
import memoize from "@RHCommerceDev/utils/memoize";
import { usePageContentNoProxy } from "custom-providers/LocationProvider";
import Chevron from "@RHCommerceDev/icon-dropdown-v2";
import { Typography } from "@RHCommerceDev/unstyled-ui-components";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuPortal
} from "@RHCommerceDev/unstyled-ui-components";
import {
  BRAND_NAME_TO_BRAND_CODE,
  NA_COUNTRIES,
  TRAILING_PARENTHESIS_REGEX
} from "@RHCommerceDev/utils/constants";
import { cn } from "@RHCommerceDev/utils/cn";

export type GlobalCountrySelectorProps = {
  isDesktop?: boolean;
  color?: string;
  pageContent?: any;
};

const GlobalCountrySelector: FC<GlobalCountrySelectorProps> = ({
  color,
  isDesktop,
  ...rest
}) => {
  const env = useEnv();
  const brand = useSite();
  const { setCountrySelector } = useCountrySite();
  const { setCookieWrapper } = useCookiesWithPermission();
  const {
    country: selectedCountry,
    language,
    setCountry: setSelectedCountry,
    setLanguage,
    setMeasurement,
    handleSaveCookies
  } = useUserPreferences();

  const [isDropdownOpen, setDropdownOpen] = useState(false);

  const FEATURE_EU_MONACO = yn(env.FEATURE_EU_MONACO);
  const FEATURE_EU_EXPANSION_FR = yn(env.FEATURE_EU_EXPANSION_FR);
  const FEATURE_MONTREAL = yn(env.FEATURE_MONTREAL);

  const monacoAsTheShippingCountry = !processEnvServer
    ? !!localStorage.getItem("shippingCountry")
    : false;

  const pageContent =
    rest?.pageContent ?? usePageContentNoProxy()?.pageContent?.globalHeader;
  const countrySelectorContent = pageContent?.countrySelectorDialog;

  //Extract `countries` and `countryAbbreviationMapper` from pageContent with fallback from countries-config
  let {
    countries: availableCountries = Object.fromEntries(
      Object.entries(countriesConfig).map(([country, data]) => [
        country,
        data.labelEN
      ])
    ),
    countryAbbreviationMapper = countryAbbreviationMapperConfig
  } = countrySelectorContent ?? {};

  // Create a mapping of country codes to cleaned-up country names by removing trailing parentheses.
  const countryNameMapper = Object.fromEntries(
    Object.entries(availableCountries).map(([key, value]) => [
      key,
      (value as string)?.replace(TRAILING_PARENTHESIS_REGEX, "")
    ])
  );

  // Filter and sort countries by brand rules, putting "US" first (like ["US", "CA", "FR", ...])
  const filteredCountryList = useMemo(() => {
    const countryList = Object.keys(availableCountries);

    if (
      brand === BRAND_NAME_TO_BRAND_CODE["BABY & CHILD"] ||
      brand === BRAND_NAME_TO_BRAND_CODE["TEEN"]
    ) {
      return countryList.filter(country => NA_COUNTRIES.includes(country));
    }

    if (!FEATURE_EU_EXPANSION_FR) {
      const index = countryList.indexOf("FR");
      if (index !== -1) countryList.splice(index, 1);
    }

    countryList.sort((a, b) => {
      if (a === "US") return -1;
      if (b === "US") return 1;
      return countryNameMapper[a].localeCompare(countryNameMapper[b]);
    });

    return countryList;
  }, [countrySelectorContent, brand, env?.FEATURE_EU_EXPANSION_FR]);

  //Prioritize the `selectedCountry` in the display list
  const countryDisplayList = useMemo(
    () => [
      selectedCountry,
      ...filteredCountryList.filter(country => country !== selectedCountry)
    ],
    [selectedCountry, filteredCountryList]
  );

  const handleCountryChange = (country: string, changedlanguage?: string) => {
    const { country: newCountry, measurement: newMeasurement } =
      countriesConfig[country].defaultValues;

    const newLanguage =
      changedlanguage || countriesConfig[country].defaultValues.language;
    document?.body?.dispatchEvent(
      new CustomEvent("cta_click", {
        detail: {
          item: {
            fromHeader: true,
            country: newCountry,
            language: newLanguage,
            measurement: newMeasurement
          }
        }
      })
    );

    setCountrySelector(true);
    setSelectedCountry(newCountry);
    setLanguage(newLanguage);
    setMeasurement(newMeasurement);

    handleSaveCookies(
      {
        country: newCountry,
        language: newLanguage,
        measurement: newMeasurement
      },
      undefined,
      undefined,
      true
    );

    setCookieWrapper("manual_country_selection", true);

    if (FEATURE_EU_MONACO && monacoAsTheShippingCountry) {
      localStorage.removeItem("shippingCountry");
    }
  };

  return (
    <DropdownMenu
      modal={false}
      open={isDropdownOpen}
      onOpenChange={setDropdownOpen}
    >
      <DropdownMenuTrigger asChild>
        <button
          onPointerDown={e => e.preventDefault()}
          onClick={() => setDropdownOpen(prev => !prev)}
          style={{ color }}
          className="inline-flex h-4 items-center justify-center gap-1 p-0 outline-none"
        >
          <Typography
            className="h-[13px] whitespace-nowrap text-[13px] capitalize leading-[120%] tracking-[0.52px]"
            fontFamily="rhc"
            fontWeight="thin"
            variant="default"
          >
            {countryAbbreviationMapper[selectedCountry]}
          </Typography>
          <Chevron style={{ color }} strokeWidth={0.75} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent
          side="top"
          className="flex -translate-y-[61px] flex-col gap-2.5 bg-rh-buff px-0 py-[42px] shadow-md"
        >
          {countryDisplayList?.map((country, index) => (
            <DropdownMenuItem
              key={index}
              onClick={() => {
                if (country != selectedCountry) {
                  handleCountryChange(country);
                }
              }}
              className={cn(
                "group pl-4 pr-3 text-rh-dark outline-none hover:text-black focus:text-black",
                {
                  "w-[134px]": FEATURE_EU_EXPANSION_FR || FEATURE_MONTREAL
                }
              )}
              style={{
                display: "grid",
                gridTemplateColumns: `${
                  country === selectedCountry ? "1fr auto" : "1fr 2fr"
                }`,
                alignItems: "baseline",
                columnGap: "10px"
              }}
            >
              <Typography
                className={cn(
                  "whitespace-nowrap text-[13px] font-thin capitalize leading-[120%] tracking-[0.52px]",
                  {
                    "text-black": country === selectedCountry
                  }
                )}
                fontFamily="rhc"
                variant="default"
              >
                {countryAbbreviationMapper[country]}
              </Typography>

              {((FEATURE_MONTREAL && country === "CA") ||
                (FEATURE_EU_EXPANSION_FR && country === "FR")) && (
                <div className="flex gap-2.5">
                  {countriesConfig[country].availableLanguages.map(
                    (lang, langIndex) => (
                      <div
                        key={langIndex}
                        className="cursor-pointer text-[#898886] transition-colors duration-200 hover:text-black"
                        onClick={e => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleCountryChange(country, lang);
                        }}
                      >
                        <Typography
                          className={cn(
                            "whitespace-nowrap text-[11px] font-thin capitalize leading-[120%] tracking-[0.52px]",
                            {
                              "text-black":
                                lang === language && country === selectedCountry
                            }
                          )}
                          fontFamily="rhc"
                          variant="default"
                        >
                          {lang.split("-")[0].toLocaleUpperCase()}
                        </Typography>
                      </div>
                    )
                  )}
                </div>
              )}

              {country === selectedCountry && (
                <Chevron
                  strokeWidth={0.75}
                  className="rotate-180 text-inherit transition-transform"
                  style={{ gridColumn: "3", alignSelf: "center" }}
                />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
};

export default memoize(GlobalCountrySelector);
